import type { Locale } from '@lims/shared-types';

/**
 * Presentation-layer formatting.
 *
 * Timestamps arrive from the API as UTC ISO 8601 and are formatted for display
 * here, in the tenant's time zone. Storage stays UTC; only the display moves.
 * Both locales use the Gregorian calendar by default, with Hijri available as
 * a secondary display behind a tenant flag.
 */

export interface FormatOptions {
  readonly locale: Locale;
  /** IANA zone from the laboratory's settings, for example `Asia/Riyadh`. */
  readonly timezone?: string;
}

/**
 * Arabic numerals rather than Eastern Arabic-Indic digits.
 *
 * `ar` alone would render ١٢٣ in most engines. Laboratory records are read
 * alongside instrument output and printed reports, where Western digits are
 * the convention, so the locale is pinned to the Latin numbering system.
 */
function intlLocale(locale: Locale): string {
  return locale === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB';
}

export function formatDateTime(
  iso: string | null | undefined,
  options: FormatOptions,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(intlLocale(options.locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(options.timezone ? { timeZone: options.timezone } : {}),
  }).format(date);
}

export function formatDate(
  iso: string | null | undefined,
  options: FormatOptions,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(intlLocale(options.locale), {
    dateStyle: 'medium',
    ...(options.timezone ? { timeZone: options.timezone } : {}),
  }).format(date);
}

/** Secondary Hijri rendering, shown only when the laboratory enables it. */
export function formatHijriDate(
  iso: string | null | undefined,
  timezone?: string,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  try {
    return new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura-nu-latn', {
      dateStyle: 'medium',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(date);
  } catch {
    // An engine without the Umm al-Qura calendar simply shows no secondary
    // date, rather than showing a wrong one.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Numbers and money
// ---------------------------------------------------------------------------

/**
 * Numbers.
 *
 * WESTERN DIGITS IN BOTH LANGUAGES. `ar` alone would render ١٢٣ in most
 * engines, and for this product that would be a mistake: a laboratory reads a
 * value on screen next to the same value on an instrument display, on a
 * supplier's certificate and on a printed report, and all of those use Western
 * digits. A locale-native rendering of the digits would make the screen the odd
 * one out, and a transcription error more likely — while translating nothing
 * anyone actually reads as language. The locale still governs the SEPARATORS
 * and the grouping, which is where locale awareness genuinely helps.
 *
 * Recorded as a Phase 2 decision in PLAN.md section 16.1c.
 */
export function formatNumber(
  value: number | null | undefined,
  options: FormatOptions & { readonly decimals?: number },
): string | null {
  // An absent value is NOT zero (CLAUDE.md section 8). It renders as nothing,
  // and the caller decides what to show in its place.
  if (value === null || value === undefined || Number.isNaN(value)) return null;

  return new Intl.NumberFormat(intlLocale(options.locale), {
    ...(options.decimals === undefined
      ? {}
      : {
          minimumFractionDigits: options.decimals,
          maximumFractionDigits: options.decimals,
        }),
  }).format(value);
}

/**
 * Money, from INTEGER MINOR UNITS.
 *
 * All money in this system is stored as an integer number of minor units —
 * halalas for SAR — so no invoice total is ever the result of adding floating
 * point values (PLAN section 16.2, risk R13). The conversion to a decimal
 * happens here, at the last possible moment, for display only.
 */
export function formatMoney(
  minorUnits: number | null | undefined,
  options: FormatOptions & { readonly currency?: string },
): string | null {
  if (minorUnits === null || minorUnits === undefined) return null;

  const currency = options.currency ?? 'SAR';
  return new Intl.NumberFormat(intlLocale(options.locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);
}

// ---------------------------------------------------------------------------
// Sizes
// ---------------------------------------------------------------------------

const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];

/**
 * A file size, in the largest unit that keeps the number readable.
 *
 * The unit NAME comes from `Intl`, not from the translation catalogue, so it
 * is correct in any language the product later adds without anyone having to
 * remember to translate four more strings. The digits stay Western in both
 * locales, for the same reason as every other number here — see
 * `formatNumber`.
 *
 * Binary multiples (1024), because that is what a filesystem and a quota
 * actually count in. Showing 1 MB for 1,000,000 bytes would make a quota
 * appear not to add up.
 */
export function formatBytes(
  bytes: number | null | undefined,
  options: FormatOptions = { locale: 'en' },
): string | null {
  // An absent size is NOT zero (CLAUDE.md section 8).
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return null;

  let value = Math.abs(bytes);
  let index = 0;
  while (value >= 1024 && index < BYTE_UNITS.length - 1) {
    value /= 1024;
    index += 1;
  }

  const unit = BYTE_UNITS[index] ?? 'byte';
  return new Intl.NumberFormat(intlLocale(options.locale), {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: index === 0 ? 0 : 1,
  }).format(bytes < 0 ? -value : value);
}
